import React, { useEffect, useRef, useState } from "react";
import ExternalPropertyCard from "components/Card/ExternalPropertyCard";
import OrganizerHeader from "./OrganizerHeader";
import useProperties from "hooks/useProperties";
import { ReactComponent as ExternalLinkIcon } from "assets/icons/external_link_big.svg";
import ExternalPropertyCardSkeleton from "components/Skeleton/ExternalPropertyCardSkeleton";
import { Tooltip } from "react-tooltip";
import RemoveWarningDialog from "components/Dialog/RemoveWarningDialog";
import { useOrganiseList } from "context/OrganiseListContext";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import useAuth from "hooks/useAuth";
import AnimatedDialogLayout from "components/Dialog/AnimatedDialogLayout";
import { ReactComponent as CloseIcon } from "assets/icons/close.svg";
import ButtonSpinner from "components/Loading/ButtonSpinner";
import {
  formatAbbreviationNumber,
  formatCurrency,
  formatCurrencyInEuro,
  formatNumericNumber,
} from "utils/formatNumber";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { ReactComponent as InfoIcon } from "assets/icons/info_2.svg";
import PropertyNoteDialog from "components/Dialog/PropertyNoteDialog";
import useBookmark from "hooks/useBookmark";

const ExternalProperties = () => {
  const {
    isAddingExternal,
    isLoadingExternal,
    externalProperties,
    saveCustomExternalProperty,
    fetchExternalProperties,
    removeExternalProperties,
    updateExternalPropertyNote,
  } = useProperties();
  const { setIsPropertyNoteDialog } = useBookmark();
  const { user } = useAuth();
  const [isRemovePropertyPopupOpen, setIsRemovePropertyPopupOpen] =
    useState(false);
  const [propertyToRemove, setPropertyToRemove] = useState(null);
  const { isFetchingList, selectedOrganiseList } = useOrganiseList();
  const [searchQuery, setSearchQuery] = useState("");
  const [openCustomExternalPropertyPopup, setOpenCustomExternalPropertyPopup] =
    useState(null);
  const [
    openEditExternalPropertyNotePopup,
    setOpenEditExternalPropertyNotePopup,
  ] = useState(null);
  const [propertyTitle, setPropertyTitile] = useState("");
  const [propertyPrice, setPropertyPrice] = useState("");
  const [propertyImage, setPropertyImage] = useState(null);
  const [dragging, setDragging] = useState(false);

  // Slider settings
  const settings = {
    modules: [Pagination],
    slidesPerView: "auto",
    pagination: false,
    freeMode: true,
    spaceBetween: 25,
    breakpoints: {
      475: {
        slidesPerView: 1,
      },
    },
  };

  // Remove external property
  const handleRemoveProperty = async () => {
    if (propertyToRemove) {
      await removeExternalProperties(propertyToRemove._id);
      setIsRemovePropertyPopupOpen(false);
      setPropertyToRemove(null);
    }
  };

  // Fetch external properties
  useEffect(() => {
    if (!isFetchingList && selectedOrganiseList) {
      fetchExternalProperties();
    }
  }, [user, isFetchingList, selectedOrganiseList]);

  const filteredProperties = externalProperties?.results?.filter((property) =>
    property.propertyId?.title
      ?.toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  // External properties pagination function
  const handleLoadMoreProperties = () => {
    if (externalProperties?.pagination?.nextPage && !isLoadingExternal) {
      fetchExternalProperties(externalProperties?.pagination?.nextPage);
    }
  };

  // Handle file selection through drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      setPropertyImage(file);
    }
  };

  // Handle file selection through file input
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPropertyImage(file);
    }
  };

  // Handle drag over to allow dropping
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleSaveExternalProperty = async () => {
    const externalUrl = openCustomExternalPropertyPopup;
    await saveCustomExternalProperty(
      propertyTitle,
      formatCurrency(propertyPrice, "EUR"),
      propertyImage,
      externalUrl
    );
    setPropertyTitile("");
    setPropertyPrice("");
    setPropertyImage("");
    setOpenCustomExternalPropertyPopup(null);
  };

  const handlePriceChange = (e) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, "");
    setPropertyPrice(rawValue);
  };

  // Update external property note
  const handleUpdateNote = async (note) => {
    let propertyId;

    if (typeof openEditExternalPropertyNotePopup.propertyId === "string") {
      propertyId = openEditExternalPropertyNotePopup.propertyId;
    } else {
      propertyId = openEditExternalPropertyNotePopup.propertyId?._id;
    }
    await updateExternalPropertyNote(propertyId, note);
    setIsPropertyNoteDialog(null);
  };

  return (
    <div className="h-full relative overflow-hidden max-w-[1550px] mx-auto w-full rounded-[10px] bg-white pb-[43px]">
      {/* Header  */}
      <OrganizerHeader
        filterId="organizedPropertiesFilter"
        isExternalProperty
        setOpenCustomExternalPropertyPopup={setOpenCustomExternalPropertyPopup}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />
      {/* property cards  */}
      <Tooltip
        id="property_info_tooltip"
        place="top"
        className="!max-w-64 !z-10 !text-sm !text-gray-500 !bg-white border !border-gray-200 !rounded-lg !shadow-sm"
      />
      <div className="mt-[13px] relative h-full md:mt-[26px]  overflow-hidden">
        {isLoadingExternal || filteredProperties?.length > 0 ? (
          <Swiper
            className="external_property_slider"
            {...settings}
            onReachEnd={handleLoadMoreProperties}
          >
            {filteredProperties?.map((property) => (
              <SwiperSlide key={property._id}>
                <ExternalPropertyCard
                  externalProperty={property}
                  setIsRemovePropertyPopupOpen={setIsRemovePropertyPopupOpen}
                  setPropertyToRemove={setPropertyToRemove}
                  setOpenEditExternalPropertyNotePopup={
                    setOpenEditExternalPropertyNotePopup
                  }
                />
              </SwiperSlide>
            ))}

            {isLoadingExternal &&
              Array.from({ length: 4 }).map((_, index) => (
                <SwiperSlide key={index}>
                  <ExternalPropertyCardSkeleton />
                </SwiperSlide>
              ))}
          </Swiper>
        ) : (
          <div className="flex h-[581px] flex-col items-center justify-center px-4">
            <ExternalLinkIcon className="w-[150px] h-[150px]  sm:w-[180px] sm:h-[180px] md:w-[236px] md:h-[236px] text-white stroke-[#4F6F8F]" />
            <p className="text-[#4F6F8F] text-center font-semibold leading-[30px] text-[24px] mt-6 sm:mt-[40px] md:mt-[60px] lg:mt-[75px]">
              Nessuna proprietà organizzata è stata salvata
            </p>
          </div>
        )}
      </div>

      <AnimatedDialogLayout
        isOpen={openCustomExternalPropertyPopup}
        onClose={() => setOpenCustomExternalPropertyPopup(null)}
      >
        <div className="max-w-lg w-[500px]">
          <div className="flex items-center justify-between">
            <h1 className="text-black font-semibold text-[18px] flex-1 text-center leading-[21.78px]">
              Aggiungi Proprietà Esterna
            </h1>
            <span
              className="cursor-pointer"
              data-tooltip-id="custom_property_add_tooltip"
              data-tooltip-content="Non siamo riusciti a recuperare le informazioni dal link inserito. Riempi manualmente le informazioni necessarie."
            >
              <InfoIcon width={15} height={15} />
            </span>
            {/* Info Tooltip  */}
            <ReactTooltip
              id="custom_property_add_tooltip"
              place="top"
              positionStrategy="fixed"
              className="!max-w-64 !z-10 !text-sm !text-gray-500 !bg-white border !border-gray-200 !rounded-lg !shadow-xs"
            />
          </div>
          <input
            value={propertyTitle}
            onChange={(e) => setPropertyTitile(e.target.value)}
            type="text"
            placeholder={"Nome Proprietà"}
            className="outline-none bg-transparent mt-[11px] border border-[#D1D1D1] rounded-[11px] h-[61px] w-full text-[16px] font-medium placeholder:text-[#949494] px-[15px]"
          />

          <div className="relative mt-[11px]">
            <input
              value={propertyPrice ? formatCurrencyInEuro(propertyPrice) : ""}
              onChange={handlePriceChange}
              type="text"
              placeholder={"Prezzo Della Proprietà"}
              className="outline-none bg-transparent  border border-[#D1D1D1] rounded-[11px] h-[61px] w-full text-[16px] font-medium placeholder:text-[#949494] pr-[15px] pl-[25px]"
            />
            <span className="absolute left-2 text-[16px] font-medium top-1/2 transform -translate-y-1/2">
              €
            </span>
          </div>
          {/* Drag-and-drop box */}
          <div
            className={`border ${
              dragging ? "border-dashed border-primary" : "border-[#D1D1D1]"
            } 
    rounded-[11px] mt-4 p-4 text-center h-[200px] flex justify-center items-center cursor-pointer relative`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={() => setDragging(false)}
            onClick={() => document.getElementById("fileInput").click()}
          >
            {propertyImage ? (
              <div className="relative w-full h-full">
                <img
                  src={URL.createObjectURL(propertyImage)}
                  alt="Uploaded"
                  className="w-full h-full object-cover rounded-[11px]"
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPropertyImage(null);
                  }}
                  className="absolute top-2 right-2 bg-white rounded-full p-2 text-black"
                >
                  <CloseIcon />
                </button>
              </div>
            ) : (
              <span className="text-[#949494]">
                Trascina e rilascia un'immagine qui o fai clic per selezionarla
              </span>
            )}
          </div>
          {/* Hidden file input */}
          <input
            type="file"
            hidden
            onChange={handleFileSelect}
            accept="image/*"
            id="fileInput"
          />
          <button
            className={`bg-primary mt-5 disabled:cursor-not-allowed text-white rounded-[11px] h-[51px] w-full grid justify-center items-center font-semibold text-[18px]`}
            onClick={handleSaveExternalProperty}
            disabled={isAddingExternal}
          >
            {isAddingExternal ? <ButtonSpinner /> : "Aggiungi Proprietà"}
          </button>
        </div>
      </AnimatedDialogLayout>
      <PropertyNoteDialog
        id={openEditExternalPropertyNotePopup?._id}
        onClick={handleUpdateNote}
        loading={isAddingExternal}
        defaultText={openEditExternalPropertyNotePopup?.note || ""}
      />
      <RemoveWarningDialog
        warningText={`Sei sicuro di voler eliminare ${propertyToRemove?.title} dalla lista ${selectedOrganiseList?.name}?`}
        isOpen={isRemovePropertyPopupOpen}
        isClose={() => {
          setPropertyToRemove(null);
          setIsRemovePropertyPopupOpen(false);
        }}
        onClick={handleRemoveProperty}
        buttonText2="Annulla"
      />
    </div>
  );
};

export default ExternalProperties;
